import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { GetPdpProductRequest, type PdpProductAPI } from 'components/discovery/pdp/pdp-product/pdp-product-api.js'
import { productPath, searchPath } from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import type { StorefrontAPIErrorResponse } from 'components/discovery/shared/ecommerce-api-token/api/ecommerce-api-token-api.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import { VariantMatrixAPI, VariantMatrixSearchRequest } from './variant-matrix-api.js'
import type {
  VariantMatrixFilter,
  VariantMatrixFilterOption,
  VariantMatrixProduct,
  VariantMatrixSearchResults,
  VariantMatrixServiceContract,
} from './typing/variant-matrix-typing.js'

function isApiError(value: unknown): value is StorefrontAPIErrorResponse {
  return typeof value === 'object' && value != null && 'api_error_code' in value
}

function mapStockIndication(
  stock: { indication?: VariantMatrixProduct['stock']['indication'] },
): Pick<VariantMatrixProduct['stock'], 'indication'> {
  const indication = stock.indication
  if (!indication?.style || !indication.icon || !indication.text) {
    return {}
  }

  return {
    indication: {
      ...(indication.rule_id != null && { rule_id: indication.rule_id }),
      style: indication.style,
      icon: indication.icon,
      text: indication.text,
      ...(indication.tooltip != null && { tooltip: indication.tooltip }),
    },
  }
}

function mapPdpProductToMatrixProduct(product: PdpProduct): VariantMatrixProduct {
  return {
    id: product.id,
    code: product.code,
    name: product.name,
    page_link: product.page_link,
    variant: {
      attributes: (product.variant?.attributes ?? []).map(attr => ({
        name: attr.name,
        value: attr.value,
      })),
    },
    prices: {
      currency_code: product.prices.currency_code ?? 'EUR',
      sale: product.prices.sale ?? '',
      price_per_price_uom: false,
      price_uom: product.ordering.default_uom,
    },
    ordering: {
      default_uom: product.ordering.default_uom,
      ...(product.ordering.order_quantity != null && { order_quantity: product.ordering.order_quantity }),
      ...(product.ordering.minimum_order_quantity != null && { minimum_order_quantity: product.ordering.minimum_order_quantity }),
      ...(product.ordering.cannot_be_added_to_basket != null && { cannot_be_added_to_basket: product.ordering.cannot_be_added_to_basket }),
    },
    tags: product.tags.map(tag => ({ name: tag.name })),
    assets: [],
    stock: {
      amount: product.stock.amount ?? 0,
      ...(product.stock.message != null && { message: product.stock.message }),
      ...mapStockIndication(product.stock),
    },
    status: {
      can_order_product: !product.ordering.cannot_be_added_to_basket,
      message: '',
    },
    picture: { mini: product.picture.mini, normal: product.picture.normal },
  }
}

const resolveSearchUrl = (url: string, searchEndpoint: string): string => {
  if (!url) return url

  const normalized = url.replace('/search/products/', '/search/')
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return normalized
  }

  return new URL(normalized, searchEndpoint).toString()
}

export class VariantMatrixService implements VariantMatrixServiceContract {
  private initialAttributes: VariantMatrixFilter[] | null = null
  private stockOnly = false
  private lastFilterUrl: string | null = null
  private readonly searchEndpoint = createUrl(searchPath()).then(url => url.href)

  constructor(
    private productApi: PdpProductAPI,
    private matrixApi: VariantMatrixAPI,
    private variantGroupId: number,
    private filterAttributes: number[],
    private displayLimit: number,
    private sortMode: string,
  ) {}

  async getProductById(productId: number): Promise<VariantMatrixProduct> {
    const result = await this.productApi.getProduct(new GetPdpProductRequest(await this.resolveProductUrl(productId)))
    if (isApiError(result)) {
      throw new Error(result.message)
    }
    return mapPdpProductToMatrixProduct(result)
  }

  private async resolveProductUrl(productId: number): Promise<string> {
    const url = await createUrl(productPath(productId))
    url.searchParams.append('features', 'prices')
    url.searchParams.append('features', 'stock')
    return url.href
  }

  private buildDefaultSearchUrl(searchEndpoint: string): string {
    const params = new URLSearchParams({
      variant_id: String(this.variantGroupId),
      ignore_variant_grouping: 'true',
    })
    if (this.sortMode) params.set('sort_mode', this.sortMode)
    params.set('page_size', String(this.displayLimit))
    params.set('show_hidden_attributes', 'true')
    return `${searchEndpoint}?${params.toString()}`
  }

  async getInitialSearchResults(): Promise<VariantMatrixSearchResults> {
    const searchEndpoint = await this.searchEndpoint
    const url = this.applyStockParam(this.buildDefaultSearchUrl(searchEndpoint))
    this.lastFilterUrl = url
    const searchResults = await this.fetchData(url, searchEndpoint)
    this.initialAttributes = [...searchResults.attributes]
    return searchResults
  }

  async getSearchResults(
    url: string,
    options?: { isPagination?: boolean; inStock?: boolean },
  ): Promise<VariantMatrixSearchResults> {
    const searchEndpoint = await this.searchEndpoint
    const inStock = options?.inStock ?? this.stockOnly
    const resolvedUrl = this.applyStockParam(resolveSearchUrl(url, searchEndpoint), inStock)
    if (!options?.isPagination) {
      this.lastFilterUrl = resolvedUrl
    }
    return this.fetchData(resolvedUrl, searchEndpoint)
  }

  setStockOnly(inStock: boolean): void {
    this.stockOnly = inStock
  }

  async refreshCurrentResults(): Promise<VariantMatrixSearchResults> {
    const searchEndpoint = await this.searchEndpoint
    const parsed = new URL(this.lastFilterUrl ?? this.buildDefaultSearchUrl(searchEndpoint))
    parsed.searchParams.delete('page')
    const url = this.applyStockParam(parsed.toString())
    this.lastFilterUrl = url
    return this.fetchData(url, searchEndpoint)
  }

  private applyStockParam(url: string, inStock: boolean = this.stockOnly): string {
    if (!url) return url
    const parsed = new URL(url)
    parsed.searchParams.delete('in_stock')
    if (inStock) {
      parsed.searchParams.set('in_stock', 'true')
    }
    return parsed.toString()
  }

  private constructFilters(fetchedAttributes: VariantMatrixFilter[], searchEndpoint: string): VariantMatrixFilter[] {
    if (!fetchedAttributes) return []

    let attributes: VariantMatrixFilter[] = [
      ...fetchedAttributes
        .filter(attr => this.filterAttributes.includes(attr.id))
        .map(attr => ({
          id: attr.id,
          name: attr.name,
          options: (attr.values ?? attr.options ?? []).map(option => ({
            id: option.id,
            value: resolveSearchUrl(option.api_link ?? option.value, searchEndpoint),
            name: option.name,
            selected: option.selected,
          })),
        })),
    ].sort((a, b) => this.filterAttributes.indexOf(a.id) - this.filterAttributes.indexOf(b.id))

    if (this.initialAttributes) {
      let index = 0
      const mergedAttributes: VariantMatrixFilter[] = []
      for (const attribute of this.initialAttributes) {
        const mergedAttribute: VariantMatrixFilter = { ...attribute, options: [...(attribute.options ?? [])] }
        const mergedOptions: VariantMatrixFilterOption[] = []
        for (const item of attribute.options ?? []) {
          const mergedItem: VariantMatrixFilterOption = { ...item, unavailable: false }
          const filteredOption = this.getOptionById(attributes[index]?.options ?? [], item.id)
          if (filteredOption === null) {
            mergedItem.unavailable = true
          } else {
            mergedItem.selected = filteredOption.selected
            mergedItem.value = filteredOption.value
          }
          mergedOptions.push(mergedItem)
        }
        mergedAttribute.options = mergedOptions
        mergedAttributes.push(mergedAttribute)
        index += 1
      }
      attributes = mergedAttributes
    }

    return attributes
  }

  private async fetchData(path: string, searchEndpoint: string): Promise<VariantMatrixSearchResults> {
    const res = await this.matrixApi.getSearchResults(new VariantMatrixSearchRequest(path))
    const attributes = this.constructFilters(res.attributes, searchEndpoint)

    return {
      attributes,
      products: res.products ?? [],
      selected_filters: (res.selected_filters ?? []).map(filter => ({
        ...filter,
        api_link: resolveSearchUrl(filter.api_link, searchEndpoint),
      })),
      sort_modes: (res.sort_modes ?? []).map(mode => ({
        name: mode.name,
        selected: mode.selected,
        api_link: resolveSearchUrl(mode.search_link ?? mode.api_link, searchEndpoint),
      })),
      paginator: {
        has_next: res.paginator?.has_next ?? false,
        next_page_api_link: res.paginator?.next_page_api_link
          ? resolveSearchUrl(res.paginator.next_page_search_link ?? res.paginator.next_page_api_link, searchEndpoint)
          : '',
      },
      total_found: res.total_found ?? res.products?.length ?? 0,
    }
  }

  private getOptionById(options: VariantMatrixFilterOption[], id: number): VariantMatrixFilterOption | null {
    for (const option of options) {
      if (option.id === id) return option
    }
    return null
  }
}
